// components/shared/AccessibilityWidget.js
// כפתור נגישות צף (FAB) — 4 הגדרות:
// 1. גודל פונט: רגיל / גדול / גדול מאוד
// 2. ניגודיות גבוהה
// 3. הדגשת קישורים
// 4. עצירת אנימציות

import { useState, useEffect, useCallback } from 'react';
import {
  Fab,
  Popover,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  FormControlLabel,
  Divider,
  Button,
  Tooltip,
} from '@mui/material';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import {
  loadAccessibility,
  saveAccessibility,
  applyAccessibilityToDOM,
  defaultAccessibility,
  accessibilityGlobalCSS,
} from '../../config/accessibility';

export default function AccessibilityWidget() {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [settings, setSettings] = useState(defaultAccessibility);
  const [cssInjected, setCssInjected] = useState(false);

  // טעינה ראשונית
  useEffect(() => {
    const loaded = loadAccessibility();
    setSettings(loaded);
    applyAccessibilityToDOM(loaded);
  }, []);

  // הזרקת CSS גלובלי פעם אחת
  useEffect(() => {
    if (cssInjected) return;
    const style = document.createElement('style');
    style.id = 'madad-accessibility-css';
    style.textContent = accessibilityGlobalCSS;
    document.head.appendChild(style);
    setCssInjected(true);

    return () => {
      const existing = document.getElementById('madad-accessibility-css');
      if (existing) existing.remove();
    };
  }, [cssInjected]);

  // שמירה והחלה בכל שינוי
  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => {
      const next = { ...prev, [key]: value };
      saveAccessibility(next);
      applyAccessibilityToDOM(next);
      return next;
    });
  }, []);

  // איפוס
  const handleReset = useCallback(() => {
    setSettings({ ...defaultAccessibility });
    saveAccessibility(defaultAccessibility);
    applyAccessibilityToDOM(defaultAccessibility);
  }, []);

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={t('nav.accessibility')} placement="right">
        <Fab
          data-testid="shared__accessibility-widget__button--toggle"
          size="medium"
          color="primary"
          aria-label={t('nav.accessibility')}
          onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
          sx={{
            position: 'fixed',
            bottom: 24,
            // RTL: sidebar בצד ימין. right בקוד → left ויזואלית (stylis flip) → מול ה-sidebar
            right: 24,
            zIndex: 1300,
          }}
        >
          <AccessibilityNewIcon />
        </Fab>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        data-testid="shared__accessibility-widget__popover"
        slotProps={{
          paper: {
            sx: { p: 3, width: 320, borderRadius: 3 },
          },
        }}
      >
        <Typography variant="h6" gutterBottom>
          {t('accessibility.title')}
        </Typography>

        {/* גודל פונט */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('accessibility.fontSize')}
        </Typography>
        <ToggleButtonGroup
          data-testid="shared__accessibility-widget__toggle--font-size"
          value={settings.fontSize}
          exclusive
          onChange={(e, val) => {
            if (val) updateSetting('fontSize', val);
          }}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        >
          <ToggleButton value="normal" data-testid="shared__accessibility-widget__button--font-normal">
            {t('accessibility.fontSizeNormal')}
          </ToggleButton>
          <ToggleButton value="large" data-testid="shared__accessibility-widget__button--font-large">
            {t('accessibility.fontSizeLarge')}
          </ToggleButton>
          <ToggleButton value="extraLarge" data-testid="shared__accessibility-widget__button--font-xl">
            {t('accessibility.fontSizeExtraLarge')}
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider sx={{ my: 1.5 }} />

        {/* ניגודיות גבוהה */}
        <FormControlLabel
          data-testid="shared__accessibility-widget__switch--high-contrast"
          control={
            <Switch
              checked={settings.highContrast}
              onChange={(e) => updateSetting('highContrast', e.target.checked)}
            />
          }
          label={t('accessibility.highContrast')}
          sx={{ width: '100%', justifyContent: 'space-between', ml: 0, mr: 0 }}
          labelPlacement="start"
        />

        {/* הדגשת קישורים */}
        <FormControlLabel
          data-testid="shared__accessibility-widget__switch--highlight-links"
          control={
            <Switch
              checked={settings.highlightLinks}
              onChange={(e) => updateSetting('highlightLinks', e.target.checked)}
            />
          }
          label={t('accessibility.highlightLinks')}
          sx={{ width: '100%', justifyContent: 'space-between', ml: 0, mr: 0 }}
          labelPlacement="start"
        />

        {/* עצירת אנימציות */}
        <FormControlLabel
          data-testid="shared__accessibility-widget__switch--stop-animations"
          control={
            <Switch
              checked={settings.stopAnimations}
              onChange={(e) => updateSetting('stopAnimations', e.target.checked)}
            />
          }
          label={t('accessibility.stopAnimations')}
          sx={{ width: '100%', justifyContent: 'space-between', ml: 0, mr: 0 }}
          labelPlacement="start"
        />

        <Divider sx={{ my: 1.5 }} />

        {/* כפתור איפוס */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            data-testid="shared__accessibility-widget__button--reset"
            size="small"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            color="inherit"
          >
            {t('accessibility.reset')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
